// LT-520 — Test Agent (WebRTC) state machine + LiveKit room lifecycle.
//
// The hook owns three concerns that React state alone can't reach:
//   1. The livekit-client `Room` instance (long-lived, refs only).
//   2. The duration timer + auto-disconnect timer (interval refs).
//   3. The hidden <audio> element used to play the agent's remote track.
//
// Component-facing surface mirrors useTestPromptModal where it makes sense
// (call status / duration / start / stop / close handlers) but drops the
// PSTN-specific bits (phone input, status polling).
import { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, type RemoteTrack } from 'livekit-client';

import { conversationAPI } from '../../services/api';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import type { WebTestPhase } from './webTestAgentModalTypes';

const WARNING_LEAD_SECONDS = 30;

// The gateway can accept the WEB session (200 + token) yet its agent worker
// may never join the LiveKit room — when the agent-runtime is at capacity /
// OOM-killed (the AU agent-dispatcher capacity issue). In that case no remote
// audio track is ever subscribed AND RoomEvent.Disconnected never fires (the
// room stays open with only the browser in it), so without this watchdog the
// modal sits on "Talking with the agent" in total silence until the 5-minute
// hard cap. Surface it as a clear, retryable error instead. 12s is generous
// headroom — a healthy agent publishes its first audio within a few seconds —
// while bailing long before the duration cap.
const AGENT_JOIN_TIMEOUT_MS = 12000;

interface UseWebTestAgentModalResult {
  phase: WebTestPhase;
  error: string | null;
  callDuration: number;
  secondsRemaining: number | null;
  micPermissionDenied: boolean;
  isMuted: boolean;
  showWarning: boolean;
  resolvedVoiceId: string | null;
  creditsDeducted: number | null;
  startCall: () => Promise<void>;
  toggleMute: () => Promise<void>;
  endCall: () => Promise<void>;
  handleClose: () => Promise<void>;
  formatDuration: (seconds: number) => string;
}

export function useWebTestAgentModal(
  isOpen: boolean,
  onClose: () => void,
  campaignId: string,
): UseWebTestAgentModalResult {
  const [phase, setPhase] = useState<WebTestPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [maxDurationSeconds, setMaxDurationSeconds] = useState<number | null>(null);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [resolvedVoiceId, setResolvedVoiceId] = useState<string | null>(null);
  const [creditsDeducted, setCreditsDeducted] = useState<number | null>(null);

  const roomRef = useRef<Room | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const callStartTimeRef = useRef<Date | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxDurationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deductionFiredRef = useRef(false);
  // Set true once the agent's remote audio track subscribes — the signal the
  // agent actually joined. The join watchdog reads it to tell "agent never
  // joined" from a real (possibly slow) join.
  const agentAudioSeenRef = useRef(false);
  const watchdogTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const secondsRemaining = (() => {
    if (maxDurationSeconds === null) return null;
    return Math.max(0, maxDurationSeconds - callDuration);
  })();
  const showWarning =
    phase === 'active'
    && secondsRemaining !== null
    && secondsRemaining > 0
    && secondsRemaining <= WARNING_LEAD_SECONDS;

  /** Stop the duration timer + any pending auto-disconnect. Safe to call repeatedly. */
  const stopTimers = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }
    // The join watchdog is cleared here too, so teardown / manual End / a real
    // Disconnected / the max-duration cap all cancel it for free.
    if (watchdogTimeoutRef.current) {
      clearTimeout(watchdogTimeoutRef.current);
      watchdogTimeoutRef.current = null;
    }
  }, []);

  /** Tear down the room + audio. Caller separately handles the credit POST. */
  const teardownRoom = useCallback(async () => {
    stopTimers();
    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
        audioElRef.current.srcObject = null;
      } catch {
        // audio element may already be detached
      }
      audioElRef.current = null;
    }
    if (roomRef.current) {
      try {
        await roomRef.current.disconnect();
      } catch {
        // best-effort; the room may already be disconnected
      }
      roomRef.current = null;
    }
  }, [stopTimers]);

  /** Fire the credit-deduction POST exactly once per session. */
  const deductCreditsOnce = useCallback(async () => {
    if (deductionFiredRef.current) return;
    if (!sessionIdRef.current || !callStartTimeRef.current) return;
    deductionFiredRef.current = true;
    const duration = Math.max(
      0,
      Math.floor((Date.now() - callStartTimeRef.current.getTime()) / 1000),
    );
    try {
      const resp = await conversationAPI.endTestPromptWebCall(
        sessionIdRef.current,
        duration,
      );
      if (typeof resp?.credits_deducted === 'number') {
        setCreditsDeducted(resp.credits_deducted);
      }
    } catch {
      // best-effort; LT-521 covers the abuse-vector follow-up
    }
  }, []);

  const startCall = useCallback(async () => {
    setError(null);
    setMicPermissionDenied(false);
    setCreditsDeducted(null);
    setCallDuration(0);
    setIsMuted(false);
    setPhase('connecting');

    try {
      const response = await conversationAPI.testPromptWeb({ campaign_id: campaignId });
      if (
        !response?.success
        || !response.session_id
        || !response.livekit_url
        || !response.participant_token
      ) {
        throw new Error(response?.error || 'Failed to start test session.');
      }

      sessionIdRef.current = response.session_id;
      setMaxDurationSeconds(response.max_duration_seconds ?? null);
      setResolvedVoiceId(response.voice_id ?? null);
      deductionFiredRef.current = false;
      agentAudioSeenRef.current = false;

      const room = new Room({ adaptiveStream: true });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (remoteTrack: RemoteTrack) => {
        if (remoteTrack.kind === 'audio') {
          // First agent audio = the agent joined. Cancel the join watchdog,
          // but leave the duration / max-duration timers running for the call.
          agentAudioSeenRef.current = true;
          if (watchdogTimeoutRef.current) {
            clearTimeout(watchdogTimeoutRef.current);
            watchdogTimeoutRef.current = null;
          }
          const el = remoteTrack.attach() as HTMLAudioElement;
          el.autoplay = true;
          // Hidden — only the audio matters; we don't render a `<audio>` UI.
          el.style.display = 'none';
          document.body.appendChild(el);
          audioElRef.current = el;
        }
      });

      room.on(RoomEvent.Disconnected, () => {
        // Fired by the agent disconnecting OR by our own disconnect() call.
        // Either way, settle the session.
        stopTimers();
        deductCreditsOnce().finally(() => {
          // Don't clobber a terminal 'error' the join watchdog already set —
          // teardownRoom() -> disconnect() re-enters this handler.
          setPhase((p) => (p === 'error' ? 'error' : 'ended'));
        });
      });

      await room.connect(response.livekit_url, response.participant_token);

      try {
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (micErr: unknown) {
        // Surfaced separately so the UI can show a clear "enable in browser
        // settings" panel rather than the generic error toast.
        setMicPermissionDenied(true);
        setError(
          micErr instanceof Error && micErr.message
            ? micErr.message
            : 'Microphone access was denied.',
        );
        setPhase('error');
        await teardownRoom();
        return;
      }

      callStartTimeRef.current = new Date();
      durationIntervalRef.current = setInterval(() => {
        if (callStartTimeRef.current) {
          const elapsed = Math.floor(
            (Date.now() - callStartTimeRef.current.getTime()) / 1000,
          );
          setCallDuration(elapsed);
        }
      }, 1000);

      if (response.max_duration_seconds && response.max_duration_seconds > 0) {
        maxDurationTimeoutRef.current = setTimeout(() => {
          // Backend hard-cap reached — disconnect ourselves; the
          // RoomEvent.Disconnected handler fires the credit POST.
          if (roomRef.current) {
            roomRef.current.disconnect().catch(() => {
              // no-op — fallthrough into onDisconnected
            });
          }
        }, response.max_duration_seconds * 1000);
      }

      setPhase('active');

      // Arm the agent-join watchdog. The session is dispatched and we're in
      // the room, but if the agent worker never joins (capacity / OOM) no
      // audio track subscribes and Disconnected never fires — so bail with a
      // clear, retryable error instead of a silent "agent is listening" UI.
      watchdogTimeoutRef.current = setTimeout(() => {
        if (agentAudioSeenRef.current) return;
        setMicPermissionDenied(false);
        setError(
          'The agent could not connect — the voice service may be busy. '
          + 'Please try again in a moment.',
        );
        setPhase('error');
        // teardownRoom() clears timers + disconnects; the Disconnected handler
        // settles credits and won't overwrite the 'error' phase set above.
        void teardownRoom();
      }, AGENT_JOIN_TIMEOUT_MS);

      track(AnalyticsEvents.AGENT_TEST_CALL, {
        action: 'start',
        mode: 'web',
        campaign_id: campaignId,
      });
    } catch (e: unknown) {
      type AxiosLike = { response?: { data?: { detail?: unknown; error?: string } } };
      const err = e as AxiosLike & { message?: string };
      const detail = err.response?.data?.detail;
      const message =
        (Array.isArray(detail) ? detail[0] : typeof detail === 'string' ? detail : null)
        ?? err.response?.data?.error
        ?? err.message
        ?? 'Could not start the test call.';
      setError(String(message));
      setPhase('error');
      await teardownRoom();
    }
  }, [campaignId, deductCreditsOnce, stopTimers, teardownRoom]);

  const toggleMute = useCallback(async () => {
    if (!roomRef.current || phase !== 'active') return;
    const next = !isMuted;
    try {
      await roomRef.current.localParticipant.setMicrophoneEnabled(!next);
      setIsMuted(next);
    } catch {
      // best-effort — leave the toggle alone if the API errors
    }
  }, [isMuted, phase]);

  const endCall = useCallback(async () => {
    if (phase === 'ended' || phase === 'idle') return;
    setPhase('ending');
    track(AnalyticsEvents.AGENT_TEST_CALL, {
      action: 'end',
      mode: 'web',
      duration_seconds: callDuration,
    });
    await teardownRoom();
    await deductCreditsOnce();
    setPhase('ended');
  }, [callDuration, deductCreditsOnce, phase, teardownRoom]);

  const handleClose = useCallback(async () => {
    if (phase === 'active' || phase === 'connecting' || phase === 'ending') {
      await teardownRoom();
      await deductCreditsOnce();
    }
    setPhase('idle');
    setError(null);
    setMicPermissionDenied(false);
    setCallDuration(0);
    setMaxDurationSeconds(null);
    setResolvedVoiceId(null);
    setCreditsDeducted(null);
    sessionIdRef.current = null;
    callStartTimeRef.current = null;
    deductionFiredRef.current = false;
    agentAudioSeenRef.current = false;
    onClose();
  }, [deductCreditsOnce, onClose, phase, teardownRoom]);

  // Reset the hook's local state when the modal closes from the outside.
  useEffect(() => {
    if (!isOpen) {
      // teardown is best-effort here — the explicit handleClose path is
      // the primary cleanup point; this is the fallback for "parent
      // closes us by flipping isOpen".
      teardownRoom();
      sessionIdRef.current = null;
      callStartTimeRef.current = null;
      deductionFiredRef.current = false;
      agentAudioSeenRef.current = false;
      setPhase('idle');
      setError(null);
      setMicPermissionDenied(false);
      setCallDuration(0);
      setMaxDurationSeconds(null);
      setResolvedVoiceId(null);
      setCreditsDeducted(null);
      setIsMuted(false);
    }
    // teardownRoom is stable (only uses refs + stable stopTimers).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return {
    phase,
    error,
    callDuration,
    secondsRemaining,
    micPermissionDenied,
    isMuted,
    showWarning,
    resolvedVoiceId,
    creditsDeducted,
    startCall,
    toggleMute,
    endCall,
    handleClose,
    formatDuration,
  };
}
